/**
 * Process Parameters Editor
 * 
 * Reusable component for editing process-level parameters
 */

import React from 'react';
import { Card, Collapse, Space, Button, Empty } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { ProcessParameters, getProcessParameterValue } from '../../types/processParameters';
import { ParameterDefinitionEditor } from './ParameterDefinitionEditor';
import { createParameterDefinition, ParameterDefinition } from '../../types/transitionParameters';
import './ProcessParametersEditor.css';

const { Panel } = Collapse;

interface ProcessParametersEditorProps {
  parameters: ProcessParameters;
  onChange: (parameters: ProcessParameters) => void;
  mode?: 'prime' | 'company';  // Prime defines defaults, Company defines actuals
}

export const ProcessParametersEditor: React.FC<ProcessParametersEditorProps> = ({
  parameters,
  onChange,
  mode = 'company'
}) => {
  const handleParameterChange = (category: string, paramName: string, value: any) => {
    onChange({
      ...parameters,
      [paramName]: value
    });
  };

  const handleAddParameter = (category: string) => {
    const newParamName = prompt('Enter parameter name:');
    if (!newParamName) return;
    
    onChange({
      ...parameters,
      [newParamName]: createParameterDefinition('number')
    });
  };

  const handleDeleteParameter = (paramName: string) => {
    const updated = { ...parameters };
    delete updated[paramName];
    onChange(updated);
  };

  // Define parameter categories
  const categories = [
    {
      key: 'capacity',
      title: 'Capacity & Throughput',
      icon: '⚡',
      parameters: ['capacity', 'utilization_target', 'batch_size', 'setup_time', 'cycle_time']
    },
    {
      key: 'scheduling',
      title: 'Scheduling',
      icon: '📅',
      parameters: ['operating_hours', 'shifts_per_day', 'availability', 'planned_downtime']
    },
    {
      key: 'quality',
      title: 'Quality',
      icon: '✓',
      parameters: ['defect_rate', 'rework_rate', 'scrap_rate', 'first_pass_yield']
    },
    {
      key: 'cost',
      title: 'Cost',
      icon: '💰',
      parameters: ['labor_cost_per_hour', 'overhead_rate', 'setup_cost', 'variable_cost_per_unit']
    },
    {
      key: 'inventory',
      title: 'Inventory',
      icon: '📦',
      parameters: ['safety_stock', 'reorder_point', 'lead_time']
    }
  ];

  // Find custom parameters (not in predefined categories)
  const predefinedParams = new Set(categories.flatMap(c => c.parameters));
  const customParams = Object.keys(parameters).filter(key => !predefinedParams.has(key));

  return (
    <div className="process-parameters-editor">
      <Collapse defaultActiveKey={['capacity']} ghost>
        {categories.map(category => {
          const categoryParams = category.parameters.filter(p => parameters[p as keyof ProcessParameters]);
          const hasParams = categoryParams.length > 0;

          return (
            <Panel
              key={category.key}
              header={
                <Space>
                  <span>{category.icon}</span>
                  <strong>{category.title}</strong>
                  <span style={{ fontSize: '12px', color: '#888' }}>
                    ({categoryParams.length} {categoryParams.length === 1 ? 'parameter' : 'parameters'})
                  </span>
                </Space>
              }
            >
              {hasParams ? (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  {category.parameters.map(paramName => {
                    const param = parameters[paramName as keyof ProcessParameters];
                    if (!param) return null;
                    
                    // Skip config objects (not ParameterDefinition)
                    if (paramName === 'duration_behavior_config' || paramName === 'artifact_generation_config') {
                      return null;
                    }
                    
                    // Only render if it's a ParameterDefinition
                    if (!('value' in param && 'type' in param)) {
                      return null;
                    }

                    return (
                      <Card
                        key={paramName}
                        size="small"
                        extra={
                          <Button
                            type="text"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => handleDeleteParameter(paramName)}
                          />
                        }
                      >
                        <ParameterDefinitionEditor
                          label={paramName.replace(/_/g, ' ')}
                          parameter={param as ParameterDefinition}
                          onChange={(value) => handleParameterChange(category.key, paramName, value)}
                          mode={mode}
                        />
                      </Card>
                    );
                  })}
                </Space>
              ) : (
                <Empty
                  description={`No ${category.title.toLowerCase()} parameters defined`}
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )}
              
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => handleAddParameter(category.key)}
                style={{ marginTop: 16, width: '100%' }}
                size="small"
              >
                Add {category.title} Parameter
              </Button>
            </Panel>
          );
        })}

        {/* Custom Parameters */}
        {customParams.length > 0 && (
          <Panel
            key="custom"
            header={
              <Space>
                <span>🔧</span>
                <strong>Custom Parameters</strong>
                <span style={{ fontSize: '12px', color: '#888' }}>
                  ({customParams.length} {customParams.length === 1 ? 'parameter' : 'parameters'})
                </span>
              </Space>
            }
          >
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              {customParams.map(paramName => {
                const param = parameters[paramName as keyof ProcessParameters];
                if (!param) return null;
                
                // Skip config objects (not ParameterDefinition)
                if (paramName === 'duration_behavior_config' || paramName === 'artifact_generation_config') {
                  return null;
                }
                
                // Only render if it's a ParameterDefinition
                if (!('value' in param && 'type' in param)) {
                  return null;
                }

                return (
                  <Card
                    key={paramName}
                    size="small"
                    extra={
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteParameter(paramName)}
                      />
                    }
                  >
                    <ParameterDefinitionEditor
                      label={paramName}
                      parameter={param as ParameterDefinition}
                      onChange={(value) => handleParameterChange('custom', paramName, value)}
                      mode={mode}
                    />
                  </Card>
                );
              })}
            </Space>
          </Panel>
        )}
      </Collapse>

      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={() => handleAddParameter('custom')}
        style={{ marginTop: 16, width: '100%' }}
      >
        Add Custom Parameter
      </Button>
    </div>
  );
};

